import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { getCurrentUserRoleFromToken } from "../utils/auth";
import AddLotModal from "./Superadmin/AddLotModal";
import AddUserModal from "./Superadmin/AddUserModal";
import { DEMO_LOT_EVENT, isDemoLotActive } from "./DemoLotBanner/demoLotMode";
import "./SuperadminHeader.css";

export const SUPERADMIN_MODE_STORAGE_KEY = "superadmin_mode_enabled";
export const SUPERADMIN_MODE_EVENT = "superadmin-mode-toggle";
export const SUPERADMIN_OFFSET = 40;

const isBrowser = typeof window !== "undefined";

export const isSuperadminModeEnabled = (): boolean => {
  if (!isBrowser) {
    return false;
  }
  try {
    return sessionStorage.getItem(SUPERADMIN_MODE_STORAGE_KEY) === "true";
  } catch (error) {
    console.warn("Unable to read superadmin mode from sessionStorage", error);
    return false;
  }
};

export const setSuperadminModeEnabled = (enabled: boolean) => {
  if (!isBrowser) {
    return;
  }

  if (enabled) {
    const tokenRole = getCurrentUserRoleFromToken();
    if (tokenRole !== "superadmin") {
      console.warn("Ignoring attempt to enable superadmin mode without appropriate role");
      return;
    }
  }

  const current = isSuperadminModeEnabled();
  if (current === enabled) {
    return;
  }

  try {
    if (enabled) {
      sessionStorage.setItem(SUPERADMIN_MODE_STORAGE_KEY, "true");
    } else {
      sessionStorage.removeItem(SUPERADMIN_MODE_STORAGE_KEY);
    }
  } catch (error) {
    console.warn("Unable to update superadmin mode in sessionStorage", error);
  }

  window.dispatchEvent(
    new CustomEvent<{ enabled: boolean }>(SUPERADMIN_MODE_EVENT, {
      detail: { enabled },
    })
  );
};

const SuperadminHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const [enabled, setEnabled] = useState<boolean>(() => isSuperadminModeEnabled());
  const [isDemoLot, setIsDemoLot] = useState<boolean>(() => isDemoLotActive());
  const [isAddLotOpen, setIsAddLotOpen] = useState(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);

  useEffect(() => {
    if (!isBrowser) {
      return undefined;
    }

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setEnabled(detail.enabled);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
        setEnabled(event.newValue === "true");
      }
    };

    const handleDemoChange = (event: Event) => {
      const detail = (event as CustomEvent<{ active: boolean }>).detail;
      setIsDemoLot(detail.active);
    };

    window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener(DEMO_LOT_EVENT, handleDemoChange as EventListener);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener(DEMO_LOT_EVENT, handleDemoChange as EventListener);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const isLoginRoute = location.pathname.startsWith("/login");
  const isLotDashboard = location.pathname.startsWith("/lot-dashboard");
  const isDocsRoute = location.pathname.startsWith("/superadmin/docs");
  const isAgentsRoute = location.pathname.startsWith("/superadmin/agents");
  const accountRole = (user?.role || "").toLowerCase();
  const shouldRender = enabled && accountRole === "superadmin" && !isLoginRoute;

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const { body } = document;

    if (shouldRender) {
      body.classList.add("superadmin-mode");
      body.style.setProperty("--superadmin-offset", `${SUPERADMIN_OFFSET}px`);
    } else {
      body.classList.remove("superadmin-mode");
      body.style.removeProperty("--superadmin-offset");
    }

    return () => {
      body.classList.remove("superadmin-mode");
      body.style.removeProperty("--superadmin-offset");
    };
  }, [shouldRender]);

  if (!shouldRender || typeof document === "undefined") {
    return null;
  }

  if (!shouldRender || typeof document === "undefined") {
    return null;
  }

  const headerContent = (
    <div
      className={`superadmin-header${isDemoLot ? " is-demo-lot" : ""}`}
      role="status"
      aria-live="polite"
    >
      <div
        className={`superadmin-header-inner ${
          isLotDashboard ? "superadmin-header-inner-lot" : "superadmin-header-inner-default"
        }`}
      >
        <div className="superadmin-header-section">
          {isDemoLot ? (
            <span className="superadmin-header-demo-label">Demo Lot</span>
          ) : (
            <img
              src="/assets/superadmin/logo_superadmin.svg"
              alt="Superadmin"
              className="superadmin-logo"
            />
          )}
          <div className="superadmin-header-actions">
            {isLotDashboard ? (
              <>
                <button type="button" className="superadmin-header-button" onClick={() => setIsAddLotOpen(true)}>
                  <img src="/assets/superadmin/add-lot.svg" alt="" aria-hidden="true" />
                  <span>Add Lot</span>
                </button>
                <button type="button" className="superadmin-header-button" onClick={() => setIsAddUserOpen(true)}>
                  <img src="/assets/superadmin/add-user.svg" alt="" aria-hidden="true" />
                  <span>Add User</span>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="superadmin-header-button"
                  onClick={() => navigate("/lot-dashboard")}
                >
                  <img src="/assets/superadmin/all-lots.svg" alt="" aria-hidden="true" />
                  <span>All Lots</span>
                </button>
              </>
            )}
          </div>
        </div>
        <div className="superadmin-header-section superadmin-header-section-right">
          <button
            type="button"
            className={`superadmin-header-button ${isDocsRoute ? "is-active" : ""}`}
            onClick={() => navigate("/superadmin/docs")}
          >
            <img src="/assets/superadmin/docs.svg" alt="" aria-hidden="true" />
            <span>Docs</span>
          </button>
          <button
            type="button"
            className={`superadmin-header-button ${isAgentsRoute ? "is-active" : ""}`}
            onClick={() => navigate("/superadmin/agents")}
          >
            <img src="/assets/superadmin/agents.svg" alt="" aria-hidden="true" />
            <span>Agents</span>
          </button>
          <button
            type="button"
            className="superadmin-header-toggle"
            onClick={() => setSuperadminModeEnabled(false)}
            aria-label="Disable superadmin mode"
          >
            <img src="/assets/superadmin/superadmin_disable.svg" alt="" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(headerContent, document.body)}
      <AddLotModal
        isOpen={isAddLotOpen}
        onClose={() => setIsAddLotOpen(false)}
        onSuccess={() => setIsAddLotOpen(false)}
      />
      <AddUserModal
        isOpen={isAddUserOpen}
        onClose={() => setIsAddUserOpen(false)}
        onCreated={() => setIsAddUserOpen(false)}
        title="Create User"
      />
    </>
  );
};

export default SuperadminHeader;
